/**
 * Ordered stops list for the selected orientation. Selecting a stop drives the
 * map, inspector, and timeline. In edit mode (handlers given): drag a row to
 * reorder (the arrows stay as the keyboard/precision fallback), "+ Add stop"
 * arms click-the-map placement, "Add existing" reuses a project step.
 */
import { useEffect, useState, type DragEvent } from 'react';
import { FloatPanel, type FloatShell } from './PanelShell';
import { isNoopMove } from './layout';
import { useUnits } from '@/hooks/useUnits';
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '../Icon';
import { PortalIcons } from '../portalIcons';

export type StopRow = {
  stepId: string;
  name: string;
  hasScript: boolean;
  beatCount: number;
  actionCount: number;
  /** For the row's hover summary (location + expected dwell). */
  lat?: number;
  lon?: number;
  el?: number;
  dwellMinutes?: number;
  /** How many orientations use this step (>1 means it is shared/reused). */
  usedIn?: number;
  /** Comprehension checks authored on this stop (0 = no pill). */
  checkCount?: number;
};

type StopsPanelProps = {
  stops: StopRow[];
  selectedStepId: string | null;
  onSelect: (stepId: string) => void;
  /** Arrow fallback: move the stop at index one place up/down. */
  onReorder?: (index: number, dir: -1 | 1) => void;
  /** Drag reorder: move the stop at `from` to gap `insert` (0..length). */
  onReorderTo?: (from: number, insert: number) => void;
  /** Toggle placement mode: arm "click the map to add a stop" (or cancel it). */
  onAddStop?: () => void;
  /** Open the picker to reuse an existing step from the project. */
  onAddExisting?: () => void;
  /** Row hover action: copy the stop (see the editor's copyStop). */
  onCopy?: (stepId: string) => void;
  /** Row hover action: remove the stop from the orientation (see removeStop). */
  onRemove?: (stepId: string) => void;
  /** True while placement mode is armed (button shows "Cancel"). */
  placing?: boolean;
  /** Floating-card chrome from useEditorLayout. */
  shell: FloatShell;
};

/** One "Add stop" button: while placing it cancels; otherwise it opens a
 *  two-item menu — New (arm click-the-map placement) / Existing (picker).
 *  The menu is position:fixed so the header's overflow-x can't clip it. */
function AddStopButton({ onAddStop, onAddExisting, placing }: {
  onAddStop?: () => void;
  onAddExisting?: () => void;
  placing?: boolean;
}) {
  const [menu, setMenu] = useState<{ top: number; left: number } | null>(null);
  useEffect(() => {
    if (!menu) return;
    const close = (e: Event) => {
      // A pointerdown inside the menu must not close it before the item's
      // click fires.
      if ((e.target as Element | null)?.closest('.oe-add-menu')) return;
      setMenu(null);
    };
    // Deferred so the opening click doesn't immediately close it.
    const t = setTimeout(() => document.addEventListener('pointerdown', close), 0);
    return () => { clearTimeout(t); document.removeEventListener('pointerdown', close); };
  }, [menu]);

  if (placing) {
    return (
      <button className="oe-btn sm primary" onClick={onAddStop} title="Cancel placing">
        Cancel
      </button>
    );
  }
  return (
    <>
      <button
        className={'oe-btn sm ghost' + (menu ? ' on' : '')}
        title="Add a stop to this orientation"
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setMenu(menu ? null : { top: r.bottom + 4, left: r.right });
        }}
      >
        + Add stop
      </button>
      {menu && (
        <div className="oe-add-menu" role="menu" style={{ top: menu.top, left: menu.left }}>
          {onAddStop && (
            <button role="menuitem" onClick={() => { setMenu(null); onAddStop(); }}
              title="Click the map or 3D view to place a new stop">
              New
            </button>
          )}
          {onAddExisting && (
            <button role="menuitem" onClick={() => { setMenu(null); onAddExisting(); }}
              title="Reuse an existing stop from this project">
              Existing
            </button>
          )}
        </div>
      )}
    </>
  );
}

export function StopsPanel({ stops, selectedStepId, onSelect, onReorder, onReorderTo, onAddStop, onAddExisting, onCopy, onRemove, placing, shell }: StopsPanelProps) {
  const { fmtElevation } = useUnits();
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [over, setOver] = useState<{ index: number; after: boolean } | null>(null);
  const clearDrag = () => { setDragFrom(null); setOver(null); };

  const rowDnD = (i: number) => ({
    draggable: true,
    onDragStart: (e: DragEvent) => {
      e.dataTransfer.setData('text/plain', '');
      e.dataTransfer.effectAllowed = 'move';
      setDragFrom(i);
    },
    onDragEnd: clearDrag,
    onDragOver: (e: DragEvent<HTMLDivElement>) => {
      if (dragFrom == null) return; // not a row drag (e.g. a panel header)
      e.preventDefault();
      e.stopPropagation();
      const r = e.currentTarget.getBoundingClientRect();
      setOver({ index: i, after: e.clientY > r.top + r.height / 2 });
    },
    onDrop: (e: DragEvent) => {
      if (dragFrom == null || over == null) return;
      e.preventDefault();
      e.stopPropagation();
      const insert = over.index + (over.after ? 1 : 0);
      if (!isNoopMove(dragFrom, insert)) onReorderTo?.(dragFrom, insert);
      clearDrag();
    },
  });

  return (
    <FloatPanel
      shell={shell}
      title="Stops"
      fitContent
      headExtra={
        (onAddStop || onAddExisting) && (
          <AddStopButton onAddStop={onAddStop} onAddExisting={onAddExisting} placing={placing} />
        )
      }
    >
      <div className="oe-panel-body oe-list">
        {stops.length === 0 ? (
          <div className="oe-empty">
            <h4>No stops yet</h4>
            {onAddStop ? 'Add a stop to start authoring.' : 'This orientation has no stops.'}
          </div>
        ) : (
          stops.map((s, i) => (
            <div
              key={s.stepId}
              className={
                'oe-stop-item'
                + (s.stepId === selectedStepId ? ' sel' : '')
                + (dragFrom === i ? ' dragging' : '')
                + (dragFrom != null && over?.index === i ? (over.after ? ' drag-after' : ' drag-before') : '')
              }
              onClick={() => onSelect(s.stepId)}
              title={s.lat != null && s.lon != null
                ? `${s.lat.toFixed(5)}, ${s.lon.toFixed(5)} · ${fmtElevation(s.el ?? 0)} · dwell ${s.dwellMinutes ?? 0} min`
                : undefined}
              {...(onReorderTo ? rowDnD(i) : {})}
            >
              {onReorderTo ? (
                <span className="grip" title="Drag to reorder" aria-hidden><Icon of={Icons.action.dragHandle} size={12} /></span>
              ) : (
                <span />
              )}
              <span className="n">{String(i + 1).padStart(2, '0')}</span>
              <div style={{ minWidth: 0 }}>
                <div className="title">
                  {s.name}
                  {(s.usedIn ?? 0) > 1 && (
                    <span className="oe-reuse-badge" title={`Shared: used in ${s.usedIn} orientations`}>
                      <Icon of={PortalIcons.status.shared} size={10} /> {s.usedIn}
                    </span>
                  )}
                  {(s.checkCount ?? 0) > 0 && (
                    <span
                      className="oe-check-pill"
                      title={`${s.checkCount} comprehension check${s.checkCount === 1 ? '' : 's'} on this stop`}
                    >{s.checkCount}<Icon of={Icons.status.success} size={10} /></span>
                  )}
                </div>
                <div className="meta">
                  {s.hasScript
                    ? `${s.beatCount} beat${s.beatCount === 1 ? '' : 's'} · ${s.actionCount} action${s.actionCount === 1 ? '' : 's'}`
                    : 'no script yet'}
                </div>
              </div>
              <div className="acts">
                {onCopy && (
                  <button title="Copy stop" aria-label="Copy stop" onClick={(e) => { e.stopPropagation(); onCopy(s.stepId); }}>
                    <Icon of={Icons.action.copy} size={14} />
                  </button>
                )}
                {onRemove && ((s.usedIn ?? 0) > 1 ? (
                  <button
                    title="Remove from this orientation (the stop stays, other orientations use it)"
                    aria-label="Remove stop from this orientation"
                    onClick={(e) => { e.stopPropagation(); onRemove(s.stepId); }}
                  >
                    <Icon of={Icons.action.unlink} size={14} />
                  </button>
                ) : (
                  <button
                    className="del"
                    title="Delete stop (only this orientation uses it; you can still choose to keep it)"
                    aria-label="Delete stop"
                    onClick={(e) => { e.stopPropagation(); onRemove(s.stepId); }}
                  >
                    <Icon of={Icons.action.delete} size={14} />
                  </button>
                ))}
              </div>
              {onReorder ? (
                <div className="ord">
                  <button data-up title="Move up" aria-label="Move up" onClick={(e) => { e.stopPropagation(); onReorder(i, -1); }}><Icon of={PortalIcons.action.moveUp} size={11} /></button>
                  <button data-dn title="Move down" aria-label="Move down" onClick={(e) => { e.stopPropagation(); onReorder(i, 1); }}><Icon of={PortalIcons.action.moveDown} size={11} /></button>
                </div>
              ) : (
                <span />
              )}
            </div>
          ))
        )}
      </div>
    </FloatPanel>
  );
}
